// src/views/condominios/CondominiosList.jsx
import { useAuth, EVT_CONDOMINIOS_ATUALIZADOS } from "../../contexts/AuthContext";
import React, { useState, useEffect, useCallback } from "react";
import condominiosService from "../../services/condominiosService";
import CondominioModal from "./CondominioModal";
import DeleteModal from "../../components/ui/DeleteModal";
import PlanLimitBadge from "../../components/ui/PlanLimitBadge";

const ITEMS_PER_PAGE = 10;

function formatDate(dt) {
  if (!dt) return "-";
  const d = new Date(dt);
  return d.toLocaleDateString("pt-BR");
}

export default function CondominiosList({ onCondoSelect }) {
  const { podeEditar, condominioAtivo, trocarCondominio } = useAuth();
  const canEdit = podeEditar("condominios");
  const [novoCondoCriado, setNovoCondoCriado] = useState(null);
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

  const [search, setSearch]             = useState("");
  const [filterTipo, setFilterTipo]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage]                 = useState(1);

  const [showModal, setShowModal]       = useState(false);
  const [editData, setEditData]         = useState(null);
  const [showDelete, setShowDelete]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [modalError, setModalError]     = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await condominiosService.listar({
        search, tipo: filterTipo, status: filterStatus,
        page, limit: ITEMS_PER_PAGE, orderBy: "nmCondominio", order: "ASC",
      });
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message || "Erro ao carregar condomínios.");
    } finally {
      setLoading(false);
    }
  }, [search, filterTipo, filterStatus, page]);

  useEffect(() => { carregar(); }, [carregar]);

  const statsTotal    = pagination.total;
  const statsAtivos   = data.filter(c => !c.inativo).length;
  const statsInativos = data.filter(c =>  c.inativo).length;
  const totalPages    = pagination.totalPages || 1;

  const handleNew  = () => { setEditData(null); setShowModal(true); };
  const handleEdit = (item) => { setEditData(item); setShowModal(true); };
  const handleDeleteClick = (item) => { setDeleteTarget(item); setShowDelete(true); };

  const handleSave = async (form) => {
    if (!canEdit) return;
    setSaving(true); setModalError(null);
    try {
      const { configAprovacao, ...condoData } = form;
      const isUpdate = !!form.id;
      let resp;
      let condoId = form.id;
      if (isUpdate) resp = await condominiosService.atualizar(form.id, condoData);
      else {
        resp = await condominiosService.criar(condoData);
        condoId = resp.data?.id;
      }
      if (configAprovacao && condoId) {
        try {
          await condominiosService.salvarConfigAprovacao(condoId, configAprovacao);
        } catch (cfgErr) {
          setModalError(cfgErr.message || "Condomínio salvo, mas falhou ao gravar aprovação de contas a pagar.");
          setSaving(false);
          return;
        }
      }
      setShowModal(false);
      setPage(1);
      await carregar();
      // Notifica AuthContext / Topbar / Dashboard para recarregar a lista
      window.dispatchEvent(new CustomEvent(EVT_CONDOMINIOS_ATUALIZADOS));
      // Ao criar um NOVO condomínio, oferece trocar para ele
      if (!isUpdate) {
        const novo = resp?.data || resp?.condominio || null;
        if (novo?.id) setNovoCondoCriado(novo);
      }
    } catch (err) {
      setModalError(err.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await condominiosService.deletar(deleteTarget.id);
      setShowDelete(false);
      setDeleteTarget(null);
      await carregar();
      window.dispatchEvent(new CustomEvent(EVT_CONDOMINIOS_ATUALIZADOS));
      // Se o condomínio excluído era o ativo, limpa o switcher
      if (condominioAtivo?.id === deleteTarget?.id) trocarCondominio(null);
    } catch (err) {
      setError(err.message || "Erro ao excluir.");
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatus = async (c) => {
    if (!canEdit) return;
    try {
      await condominiosService.alterarStatus(c.id, !c.inativo);
      await carregar();
      window.dispatchEvent(new CustomEvent(EVT_CONDOMINIOS_ATUALIZADOS));
    } catch (err) { setError(err.message||"Erro ao alterar status."); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-buildings me-2" style={{ color:"var(--primary)", fontSize:22 }} />
            Condomínios
          </h1>
          <p className="page-subtitle">Gerencie todos os condomínios cadastrados no sistema</p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <PlanLimitBadge tipo="condominios"/>
          <button className="btn btn-primary" onClick={handleNew} disabled={!canEdit}>
          <i className="bi bi-plus-lg" /> Novo Condomínio
        </button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[
          { icon:"bi-buildings",  cls:"blue",   val:statsTotal,    label:"Total" },
          { icon:"bi-check-circle", cls:"green", val:statsAtivos,   label:"Ativos" },
          { icon:"bi-x-circle",   cls:"gray",   val:statsInativos, label:"Inativos" },
          { icon:"bi-layers",     cls:"orange", val:totalPages,    label:"Páginas" },
        ].map(s => (
          <div className="col-6 col-md-3" key={s.label}>
            <div className="stat-card">
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`} /></div>
              <div>
                <div className="stat-value">{s.val}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header-custom">
          <div className="search-wrapper">
            <i className="bi bi-search" />
            <input className="search-input" placeholder="Buscar por nome, CNPJ, cidade..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width:"auto", fontSize:13 }}
              value={filterTipo} onChange={e => { setFilterTipo(e.target.value); setPage(1); }}>
              <option value="">Todos os tipos</option>
              {["Residencial","Comercial","Misto","Apartamentos","Lojas","Lotes"].map(t =>
                <option key={t}>{t}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto", fontSize:13 }}
              value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={carregar} title="Atualizar">
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger m-3 mb-0 d-flex align-items-center gap-2" style={{ fontSize:13 }}>
            <i className="bi bi-exclamation-triangle-fill" /> {error}
            <button className="btn btn-sm btn-outline-danger ms-auto" onClick={carregar}>Tentar novamente</button>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-custom">
            <thead>
              <tr>
                <th>#</th><th>Condomínio</th><th>CNPJ</th><th>Tipo</th>
                <th>Cidade / UF</th><th>Unidades</th><th>Contato</th>
                <th>Vencimento</th><th>Status</th><th style={{ width:110 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-5">
                  <div className="spinner-border spinner-border-sm text-primary me-2" />
                  <span style={{ fontSize:14, color:"var(--gray-500)" }}>Carregando...</span>
                </td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-5">
                  <div style={{ color:"var(--gray-400)" }}>
                    <i className="bi bi-search" style={{ fontSize:32, display:"block", marginBottom:8 }} />
                    <span style={{ fontSize:14 }}>Nenhum condomínio encontrado</span>
                  </div>
                </td></tr>
              ) : data.map((c, idx) => (
                <tr key={c.id}>
                  <td style={{ color:"var(--gray-400)", fontSize:12 }}>{(page-1)*ITEMS_PER_PAGE+idx+1}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {c.logo && (
                        <img src={c.logo} alt="" style={{ width:32, height:32, borderRadius:4, objectFit:"cover", border:"1px solid var(--gray-200)" }}/>
                      )}
                      <div>
                        <div style={{ fontWeight:600, color:"var(--gray-800)", fontSize:13 }}>
                          {c.nmFantasia || c.nmCondominio}
                        </div>
                        {c.nmFantasia && <div style={{ fontSize:11.5, color:"var(--gray-400)" }}>{c.nmCondominio}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontFamily:"monospace", fontSize:12.5 }}>{c.cnpj || "-"}</td>
                  <td><span className="badge-tipo">{c.tpCondominio}</span></td>
                  <td>
                    <span style={{ fontSize:13 }}>{c.cidade || "-"}</span>
                    {c.uf && <span style={{ fontSize:11, color:"var(--gray-400)", marginLeft:4 }}>/ {c.uf}</span>}
                  </td>
                  <td style={{ fontSize:12 }}>
                    <span title={`Unidades cadastradas / moradores ativos`}>
                      <i className="bi bi-house me-1 text-primary"/>{c.qtdUnidades||0}
                      <span style={{color:"var(--gray-400)",margin:"0 4px"}}>•</span>
                      <i className="bi bi-people me-1 text-success"/>{c.qtdMoradores||0}
                    </span>
                  </td>
                  <td style={{ fontSize:13 }}>{c.contato || c.nmSindico || "-"}</td>
                  <td>
                    <span style={{ background:"var(--gray-100)", padding:"2px 8px", borderRadius:4, fontWeight:600, color:"var(--primary)", fontSize:12 }}>
                      Dia {String(c.diaVencimento).padStart(2,"0")}
                    </span>
                  </td>
                  <td>
                    <span className={`badge-status ${c.inativo ? "badge-inativo" : "badge-ativo"}`}
                          onClick={()=>handleStatus(c)}
                          style={{cursor: canEdit ? "pointer" : "default"}}
                          title={canEdit ? "Clique para alterar status" : ""}>
                      <i className="bi bi-circle-fill" style={{ fontSize:7 }} />
                      {c.inativo ? "Inativo" : "Ativo"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar" onClick={() => handleEdit(c)} disabled={!canEdit}>
                        <i className="bi bi-pencil" />
                      </button>
                      <button className="btn btn-outline-danger btn-sm btn-icon" title="Excluir" onClick={() => handleDeleteClick(c)} disabled={!canEdit}>
                        <i className="bi bi-trash3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination-wrapper">
          <span className="pagination-info">
            {pagination.total === 0 ? "Nenhum registro"
              : `Exibindo ${(page-1)*ITEMS_PER_PAGE+1}–${Math.min(page*ITEMS_PER_PAGE, pagination.total)} de ${pagination.total} registros`}
          </span>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page===1?"disabled":""}`}>
                <button className="page-link" onClick={() => setPage(p=>p-1)}><i className="bi bi-chevron-left" /></button>
              </li>
              {Array.from({ length:totalPages },(_,i)=>(
                <li key={i+1} className={`page-item ${page===i+1?"active":""}`}>
                  <button className="page-link" onClick={()=>setPage(i+1)}>{i+1}</button>
                </li>
              ))}
              <li className={`page-item ${page===totalPages?"disabled":""}`}>
                <button className="page-link" onClick={()=>setPage(p=>p+1)}><i className="bi bi-chevron-right" /></button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <CondominioModal show={showModal} onClose={()=>{setShowModal(false); setModalError(null);}}
        onSave={handleSave} editData={editData} saving={saving} serverError={modalError} />
      <DeleteModal show={showDelete} onClose={()=>setShowDelete(false)}
        onConfirm={handleDelete} itemName={deleteTarget?.nmFantasia||deleteTarget?.nmCondominio} loading={deleting} />

      {/* ── Modal: deseja trabalhar com o condomínio recém-criado? ── */}
      {novoCondoCriado && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-buildings text-primary me-2"/>Condomínio criado com sucesso
                </h5>
                <button type="button" className="btn-close" onClick={()=>setNovoCondoCriado(null)} />
              </div>
              <div className="modal-body">
                <p style={{fontSize:14}}>
                  Deseja trabalhar agora com o condomínio
                  {" "}<strong>{novoCondoCriado.nmFantasia || novoCondoCriado.nmCondominio}</strong>?
                </p>
                <small className="text-muted">
                  Você pode trocar a qualquer momento pelo seletor no topo da tela.
                </small>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={()=>setNovoCondoCriado(null)}>
                  Manter atual{condominioAtivo ? ` (${condominioAtivo.nmFantasia||condominioAtivo.nmCondominio})` : ""}
                </button>
                <button className="btn btn-primary" onClick={()=>{ trocarCondominio(novoCondoCriado); setNovoCondoCriado(null); }}>
                  <i className="bi bi-check-circle me-2"/>Trabalhar com este condomínio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
